import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import * as TestModule from 'modules/test';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useToast } from 'components/ui/use-toast';
import { Button } from 'components/ui/button';
import { PageHeader } from 'components/ui/page-header';
import { Panel } from 'components/ui/panel';
import { StatusBadge } from 'components/ui/status-badge';
import { testPhase } from 'components/workspace/lifecycle';
import { useAttemptCount } from 'components/workspace/useAttemptCount';
import { useIsMobile } from 'hooks/use-media-query';
import { cn } from 'lib/utils';
import { computeTestReadiness, type TestReadiness } from './testReadiness';

type AntiCheating = Partial<
  Record<
    'antiCheatingMonitor' | 'disableRightClick' | 'disableCopyPaste' | 'shuffleAnswers' | 'shuffleQuestions' | 'lockdownBrowser',
    boolean
  >
>;

interface TestShape {
  title?: string;
  status?: string;
  showResultsToRespondent?: boolean;
  antiCheating?: AntiCheating;
  settings?: { shuffleQuestions?: boolean; shuffleAnswers?: boolean };
  config?: { access?: { accessType?: string } };
}

const STATUS_KEYS = ['draft', 'active', 'published', 'finished', 'archived'];
const ACCESS_LABEL: Record<string, string> = {
  public_link: 'access_card_public_title',
  private_code: 'access_card_codes_title',
  group_password: 'access_group_password',
  team: 'access_card_group_title',
  marketplace: 'access_card_marketplace_title',
};
const GRID_COLS = ['', 'grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'];

type Translate = (key: string, opts?: Record<string, unknown>) => string;

interface ReadinessCheck {
  key: string;
  done: boolean;
  title: string;
  meta: string;
  to: string;
  missingTitle: string;
  hint: string;
  action: string;
}

const join = (parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' · ');

/** The content line used by the questions check and the details row. */
function questionsSummary(r: TestReadiness, t: Translate): string {
  return join([
    r.qCount > 0 && t('n_questions', { count: r.qCount }),
    r.subjectCount > 0 && t('n_subjects', { count: r.subjectCount }),
    r.librarySectionCount > 0 && t('hub_library_sections', { count: r.librarySectionCount }),
  ]);
}

/** The anti-cheating measures in force, as the Settings page and the exam runtime read them. */
function integritySummary(d: TestShape, t: Translate, lang: string): string {
  const anti = d.antiCheating ?? {};
  const bits = [
    anti.antiCheatingMonitor && t('overview_integrity_monitor'),
    anti.lockdownBrowser && t('hub_integrity_fullscreen'),
    anti.disableCopyPaste && t('hub_integrity_nocopy'),
    anti.disableRightClick && t('overview_integrity_right_click'),
    ((anti.shuffleQuestions ?? d.settings?.shuffleQuestions) || (anti.shuffleAnswers ?? d.settings?.shuffleAnswers)) &&
      t('hub_integrity_shuffle'),
  ];
  const text = join(bits);
  return text ? text.charAt(0).toLocaleUpperCase(lang) + text.slice(1) : t('overview_integrity_none');
}

/** A missing check: amber callout with a way to fix it. */
function MissingCheck({ check, stacked }: { check: ReadinessCheck; stacked: boolean }) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3.5 py-3 dark:border-warning-900 dark:bg-warning-900/20',
        stacked ? 'flex-col' : 'items-center',
      )}
    >
      <span className="flex min-w-0 flex-1 gap-2.5">
        <AlertTriangle aria-hidden className="mt-px h-[18px] w-[18px] shrink-0 text-warning-500" />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13.5px] font-semibold leading-[18px] text-warning-700 dark:text-warning-300">
            {check.missingTitle}
          </span>
          <span className="text-[12.5px] leading-4 text-foreground/80">{check.hint}</span>
        </span>
      </span>
      <Button variant="outline" size="xs" className={cn('shrink-0', stacked && 'h-11 w-full')} asChild>
        <Link to={check.to}>{check.action}</Link>
      </Button>
    </div>
  );
}

function ReadinessPanel({ r, checks, stacked }: { r: TestReadiness; checks: ReadinessCheck[]; stacked: boolean }) {
  const { t } = useTranslation();
  const done = checks.filter((c) => c.done);
  const missing = checks.filter((c) => !c.done);
  return (
    <Panel
      title={t('hub_ready_to_activate')}
      aside={
        <StatusBadge tone={r.allReady ? 'success' : 'warning'} dot={false} className="tabular-nums">
          {r.doneCount} / {r.total}
        </StatusBadge>
      }
      flush
    >
      {done.map((c, i) => (
        <div key={c.key} className={cn('flex items-center gap-3 px-4 py-3', i > 0 && 'border-t border-border')}>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success-600 text-white">
            <Check aria-hidden className="h-3 w-3" strokeWidth={3} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[13.5px] font-medium leading-[18px] text-foreground">{c.title}</span>
            {c.meta && <span className="truncate text-[12.5px] leading-4 text-muted-foreground">{c.meta}</span>}
          </span>
          <Link
            to={c.to}
            className="-my-2 shrink-0 py-2 text-[13px] font-medium text-foreground/80 hover:text-foreground hover:underline"
          >
            {t('edit')}
          </Link>
        </div>
      ))}
      {missing.length > 0 && (
        <div className={cn('flex flex-col gap-3 px-4 pb-4', done.length > 0 ? 'pt-3' : 'pt-4')}>
          {missing.map((c) => (
            <MissingCheck key={c.key} check={c} stacked={stacked} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function StatStrip({ stats, stacked }: { stats: Array<{ key: string; label: string; value: string }>; stacked: boolean }) {
  return (
    <section
      className={cn('grid rounded-lg border border-border bg-card', stacked ? 'grid-cols-2' : GRID_COLS[stats.length])}
    >
      {stats.map((s, i) => (
        <div
          key={s.key}
          className={cn(
            'flex min-w-0 flex-col gap-1 px-4 py-3.5',
            !stacked && i > 0 && 'border-l border-border',
            stacked && i > 1 && 'border-t border-border',
            // An odd last figure spans the row so its divider runs full width.
            stacked && stats.length % 2 === 1 && i === stats.length - 1 && 'col-span-2',
          )}
        >
          <span className="truncate text-xs text-muted-foreground">{s.label}</span>
          <span className="text-xl font-bold leading-6 tabular-nums text-foreground">{s.value}</span>
        </div>
      ))}
    </section>
  );
}

function DetailsPanel({ rows, settingsTo }: { rows: Array<{ key: string; label: string; value: string }>; settingsTo: string }) {
  const { t } = useTranslation();
  return (
    <Panel
      title={t('overview_details_title')}
      aside={
        <Link to={settingsTo} className="-my-2 py-2 font-medium text-foreground/80 hover:text-foreground hover:underline">
          {t('settings')}
        </Link>
      }
      flush
    >
      <dl>
        {rows.map((row, i) => (
          <div
            key={row.key}
            className={cn(
              'grid min-h-[42px] grid-cols-[120px_minmax(0,1fr)] items-center gap-x-4 px-4 py-2.5 text-[13.5px]',
              i > 0 && 'border-t border-border',
            )}
          >
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className="text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

function SharePanel({ shareUrl, testCode, stacked }: { shareUrl: string; testCode: string; stacked: boolean }) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('link_copied'));
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div data-tour="test-share-panel" id="test-share-panel">
      <Panel title={t('hub_share')}>
        {shareUrl ? (
          <div className="flex flex-col gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[12.5px] leading-4 text-muted-foreground">{t('hub_test_code')}</span>
              <span className="truncate font-mono text-[17px] font-semibold tracking-[0.04em] text-foreground">
                {testCode || '—'}
              </span>
              <span className="text-[13px] leading-normal text-muted-foreground">{t('hub_code_hint')}</span>
            </div>
            <div
              className={cn(
                'flex items-center gap-1 rounded-md border border-border bg-card pl-3 pr-1',
                stacked ? 'h-11' : 'h-9',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
                {shareUrl.replace(/^https?:\/\//, '')}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className={stacked ? 'h-9 w-9' : 'h-7 w-7'}
                onClick={copyLink}
                aria-label={t('copy')}
              >
                <Copy className="h-[15px] w-[15px]" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-[12.5px] leading-4 text-muted-foreground">{t('hub_test_code')}</span>
            <span aria-hidden className="font-mono text-[17px] font-semibold tracking-[0.04em] text-muted-foreground/60">
              — — —
            </span>
            <span className="text-[13px] leading-normal text-muted-foreground">{t('hub_share_after_setup')}</span>
          </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * The workspace landing page: readiness checks, a few figures, the test's key
 * settings and how to share it. Phones stack readiness → figures → details →
 * share; their Activate action is the layout's sticky bar.
 */
export const OverviewHubPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const isMobile = useIsMobile();
  const canViewResults = usePermission(PERMISSIONS.TESTS_VIEW_RESULTS);
  const { item, isLoading } = TestModule.Hooks.useSingle({ id: testId || '', enabled: Boolean(testId) });

  const d = (item ?? {}) as unknown as TestShape;
  const status = (d.status ?? 'draft').toLowerCase();
  const phase = testPhase(status);
  // The test carries no attempt stats; count them once it has been live.
  const started = useAttemptCount(testId, undefined, phase !== 'draft' && canViewResults);

  if (isLoading || !item?.id || !testId) {
    return <div className="min-h-[200px]" />;
  }

  const base = `/app/tests/${testId}`;
  const r = computeTestReadiness(item);
  const lang = i18n.language || 'uz';
  const shareUrl = r.shareToken ? `${window.location.origin}/s/${r.shareToken}` : '';
  const testCode = (r.publicLinkSlug || r.shareToken || '').toUpperCase();
  const accessType = d.config?.access?.accessType ?? '';
  const questions = questionsSummary(r, t);
  const minutes = r.duration > 0 ? `${r.duration} ${t('minutes')}` : '';

  const checks: ReadinessCheck[] = [
    {
      key: 'questions',
      done: r.hasQuestions,
      title: t('hub_check_questions'),
      meta: questions,
      to: `${base}/questions`,
      missingTitle: t('hub_no_questions'),
      hint: t('overview_hint_questions'),
      action: t('add_question'),
    },
    {
      key: 'timing',
      done: r.hasTiming,
      title: t('hub_timing_set'),
      meta: join([minutes, r.passingScore != null && t('overview_pass_mark_value', { score: r.passingScore })]),
      to: `${base}/settings`,
      missingTitle: t('hub_no_timing'),
      hint: t('overview_hint_timing'),
      action: t('hub_setup'),
    },
    {
      key: 'access',
      done: r.hasAccess,
      title: t('hub_access_configured'),
      meta: ACCESS_LABEL[accessType] ? t(ACCESS_LABEL[accessType]) : '',
      to: `${base}/access`,
      missingTitle: t('overview_no_access'),
      hint: t('overview_hint_access'),
      action: t('hub_setup_access'),
    },
  ];

  // "Boshlagan": a draft can't have been started; a live test's count needs
  // results access (without it the API would count only the viewer's own).
  const startedValue = phase === 'draft' ? r.respondents : canViewResults ? started : null;
  const stats = [
    ...(startedValue !== null ? [{ key: 'started', label: t('hub_stat_started'), value: startedValue == null ? '—' : String(startedValue) }] : []),
    { key: 'duration', label: t('hub_stat_duration'), value: r.duration > 0 ? `${r.duration} ${t('hub_min')}` : '—' },
    ...(r.passingScore != null ? [{ key: 'passmark', label: t('hub_stat_passmark'), value: `${r.passingScore}%` }] : []),
  ];

  const details = [
    { key: 'questions', label: t('questions'), value: questions || '—' },
    { key: 'duration', label: t('hub_stat_duration'), value: minutes || '—' },
    ...(r.passingScore != null ? [{ key: 'passmark', label: t('hub_stat_passmark'), value: `${r.passingScore}%` }] : []),
    {
      key: 'attempts',
      label: t('attempts'),
      value: r.attempts == null ? t('marketplace_attempts_unlimited') : t('n_attempts', { count: r.attempts }),
    },
    {
      key: 'results',
      label: t('results'),
      value: d.showResultsToRespondent === false ? t('overview_results_hidden') : t('overview_results_shown'),
    },
    { key: 'integrity', label: t('hub_integrity_label'), value: integritySummary(d, t, lang) },
  ];

  const readiness = <ReadinessPanel r={r} checks={checks} stacked={isMobile} />;
  const strip = <StatStrip stats={stats} stacked={isMobile} />;
  const summary = <DetailsPanel rows={details} settingsTo={`${base}/settings`} />;
  const share = <SharePanel shareUrl={shareUrl} testCode={testCode} stacked={isMobile} />;

  if (isMobile) {
    return (
      <div data-tour="test-config-header" id="test-overview-card" className="flex flex-col gap-3">
        {readiness}
        {strip}
        {summary}
        {share}
      </div>
    );
  }

  const title = (d.title || '').trim() || t('new_test');
  const statusLabel = STATUS_KEYS.includes(status) ? t(status) : status;

  return (
    <div data-tour="test-config-header" id="test-overview-card" className="flex flex-col gap-4">
      <PageHeader title={t('overview')} description={`${title} · ${statusLabel.toLocaleLowerCase(lang)}`} />
      {strip}
      <div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div data-tour="test-details-section" id="test-details-section" className="flex min-w-0 flex-col gap-3.5">
          {readiness}
          {summary}
        </div>
        <div className="flex min-w-0 flex-col gap-3.5">{share}</div>
      </div>
    </div>
  );
};

export default OverviewHubPage;
